// save name
// save game logs
// save last active game/location

import { userConverter, userToDoc } from 'api/converters/UserConverter';
import { Cls, Emojis, ValueCls } from 'app/constants';
import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { User, UserCredential } from 'lib/firebase';

import { setBeginAppInitialization, setSelectedScene } from 'app/slices/NavigationSlice';
import {
  isMounted,
  setAppUser,
  setUserAuthState,
  subscribeUserStatusChange,
  UserAuthState,
} from 'app/slices/UserSlice';
import { store } from 'app/store';
import { AppDispatch, AppScene, AppUser, Maybe } from 'types';
import { auth, db } from '../lib/firebase';

// import { getAuth, signInAnonymously } from 'firebase/auth';

// const auth = getAuth();
// signInAnonymously(auth)
//   .then(() => {
//     // Signed in..
//   })
//   .catch((error) => {
//     const errorCode = error.code;
//     const errorMessage = error.message;
//     // ...
//   });

export const getUserDataFromUserCredential = (user: User): AppUser => {
  const maybeDate = user.metadata.creationTime;
  const creationDate = maybeDate ? (new Date(maybeDate).getTime() ?? Date.now()) : Date.now();

  return {
    uid: user.uid,
    email: user.email ?? '',
    name: user.displayName ?? '',
    emailVerified: user.emailVerified,
    isAnonymous: user.isAnonymous,
    isEditor: false,
    createdOn: creationDate,
    modifiedOn: Date.now(),
  };
};

export const createFbAppUserFromUserCredential = async (userModel: UserCredential): Promise<AppUser> => {
  const usr = getUserDataFromUserCredential(userModel.user);
  const collectionData = collection(db, 'users').withConverter(userConverter);
  const docRef = doc(collectionData, usr.uid);

  await setDoc(docRef, userToDoc(usr));

  return usr;
};

export const createAppUser = async (user: User): Promise<AppUser> => {
  const usr = getUserDataFromUserCredential(user);
  const collectionData = collection(db, 'users').withConverter(userConverter);
  const docRef = doc(collectionData, usr.uid);

  await setDoc(docRef, userToDoc(usr));

  return usr;
};

export const updateFbAppUser = async (user: AppUser): Promise<AppUser> => {
  const userRef = doc(db, `users/${user.uid}`);
  const docUser = userToDoc(user);
  await updateDoc(userRef, docUser);
  store.dispatch(setAppUser(user));
  return user;
};

export const loadAppUser = async (uid: string): Promise<Maybe<AppUser>> => {
  const userRef = doc(db, `users/${uid}`).withConverter(userConverter);
  const docSnap = await getDoc(userRef);
  if (docSnap.exists()) {
    const appUser: AppUser = docSnap.data();
    return appUser;
  }
  const msg = `${Emojis.eyes} No such document! %c ${uid} %c`;
  console.log(msg, ValueCls, '');
  throw new Error('USER_NOT_ADDED', { cause: 'No such document!' });
};

/**
 * Wait for the user to log in blocker
 *
 * @return {Promise<boolean>} user's logged in status when completed
 * @deprecated - remove me soon
 */
// export const waitForUserToLogIn = async (): Promise<boolean> => {
//   const userAuthState = getUserAuthState(store.getState());
//   return new Promise((resolve) => {
//     if (userAuthState === UserAuthState.LoggedIn) resolve(true);
//     let numberOfTries = 0;
//     const timerId = setInterval(
//       () => {
//         const userState = getUserAuthState(store.getState());
//         if (userState === UserAuthState.LoggedIn) {
//           resolve(true);
//           clearInterval(timerId);
//         } else if (userState === UserAuthState.Error) {
//           resolve(false);
//           clearInterval(timerId);
//         } else if (userState === UserAuthState.NotLoggedIn || userState === UserAuthState.LoggedOut) {
//           resolve(false);
//           clearInterval(timerId);
//         } else if (numberOfTries > 20) {
//           console.log(`${Emojis.warning} Waited for over ${numberOfTries / 10} seconds and no luck`);
//           resolve(false);
//           clearInterval(timerId);
//         }
//         numberOfTries++;
//       },
//       100,
//       numberOfTries
//     );
//   });
// };

export const onUserStateReady = async (): Promise<boolean> => {
  await auth.authStateReady();
  return !!auth.currentUser;
};

const initializeApp = async (dispatch: AppDispatch): Promise<boolean> => {
  console.log(`${Emojis.eyes} %c initializeApp %c`, Cls.Info, '');
  dispatch(setBeginAppInitialization());
  dispatch(setSelectedScene(AppScene.Authentication));
  dispatch(subscribeUserStatusChange());
  return await onUserStateReady();
};

export const appLoader = async (dispatch: AppDispatch): Promise<{ success: boolean }> => {
  console.log(`${Emojis.voiceAmp} App Loader here`);
  const isMount = isMounted(store.getState());

  if (!isMount) {
    const result = await initializeApp(dispatch);

    if (result) {
      console.log(`${Emojis.eyes} App user %c exists %c`, Cls.Army, '');
      dispatch(setUserAuthState(UserAuthState.LoggedIn));
      return { success: true };
    } else {
      console.log(`${Emojis.eyes} App user %c does not exist %c`, Cls.Cri, '');
      dispatch(setUserAuthState(UserAuthState.NotLoggedIn));
      return { success: true };
    }
  } else {
    return { success: true };
  }
};
